'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import { useSignIn } from '@clerk/nextjs'
import { OnboardingLayout } from '@/features/onboarding'
import { ResetPasswordCodeStep } from '@/features/auth/components/reset-password-code-step'
import { ResetPasswordNewPasswordStep } from '@/features/auth/components/reset-password-new-password-step'
import { FieldDescription } from '@/components/ui/field'

type Step = 'enter-code' | 'set-password'

/**
 * Landed on from `AdminForgotPasswordDialog` via client-side navigation (same tab, same
 * `signIn` resource in memory — no reload/cross-device persistence concern). Code entry and
 * new-password entry are deliberately two separate components (`ResetPasswordCodeStep`/
 * `ResetPasswordNewPasswordStep`), never both sets of fields on screen together.
 */
export function AdminResetPasswordView() {
  const router = useRouter()
  const { signIn } = useSignIn()
  const [step, setStep] = useState<Step>('enter-code')
  // Set right before `finalizeAs()` runs (see `ResetPasswordNewPasswordStep`), so the
  // `!signIn.identifier` branch below is skipped during that window. `useSignIn()` is
  // signals-based and reactive — the moment `finalize()` starts activating the session,
  // `signIn.identifier` can already read as falsy for a render or two before the redirect
  // in `navigate` actually happens, which would otherwise flash the "no in-progress reset"
  // empty state right before landing on `/admin`.
  const [finalizing, setFinalizing] = useState(false)

  if (finalizing) {
    return (
      <OnboardingLayout title="Signing you in…" width="sm" fitContent>
        <FieldDescription>Just a moment…</FieldDescription>
      </OnboardingLayout>
    )
  }

  // No in-progress reset attempt for this identifier — e.g. the admin navigated here
  // directly, or the attempt expired — so there's nothing to verify a code against.
  if (!signIn.identifier) {
    return (
      <OnboardingLayout title="Reset your password" width="sm" fitContent>
        <FieldDescription>
          We couldn&apos;t find an in-progress password reset.{' '}
          <button
            type="button"
            onClick={() => router.push('/team/login')}
            className="text-primary font-semibold underline-offset-4 hover:underline"
          >
            Start from the Team Login page
          </button>
          .
        </FieldDescription>
      </OnboardingLayout>
    )
  }

  if (step === 'set-password') {
    return (
      <OnboardingLayout
        title="Set your new password"
        description="Choose a new password for your admin account."
        width="sm"
        fitContent
      >
        <ResetPasswordNewPasswordStep role="admin" onFinalizing={() => setFinalizing(true)} />
      </OnboardingLayout>
    )
  }

  return (
    <OnboardingLayout
      title="Reset your password"
      description="Enter the code we emailed you."
      width="sm"
      fitContent
    >
      <ResetPasswordCodeStep email={signIn.identifier} onVerified={() => setStep('set-password')} />
    </OnboardingLayout>
  )
}
